import React from 'react'
import { Navigate } from 'react-router-dom'
import { canAccessFeature, isPremiumFeature, type AppFeature } from '@hotel/shared'
import { useAuth } from '../contexts/AuthContextCore'
import { useHotel } from '../contexts/HotelContextCore'
import { PremiumLocked } from './PremiumLocked'
import { LogoSpinner } from './LogoSpinner'

interface NoHotelAccessProps {
  children: React.ReactNode
  roles?: string[]
  feature?: AppFeature
}

const PREMIUM_FEATURE_LABELS: Partial<Record<AppFeature, string>> = {
  dnr: 'Guest in DNR',
  'studio-management': 'Studio Management',
  inventory: 'Inventory Control',
}

function featureLabel(feature: AppFeature): string | undefined {
  return PREMIUM_FEATURE_LABELS[feature]
}

export function NoHotelAccess({ children, roles, feature }: NoHotelAccessProps) {
  const { user } = useAuth()
  const { availableHotels, selectedHotel, isLoading, hasLoadedHotels, selectedHotelIsPremium, selectedHotelPremiumResolved } = useHotel()

  // Show loading while checking hotel access
  if (isLoading || !hasLoadedHotels) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app dark:bg-app-dark transition-colors duration-300">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    )
  }

  // If user has access to hotels, check roles if specified
  if (availableHotels && availableHotels.length > 0) {
    // If roles are specified, check if user has required role
    if (roles && roles.length > 0) {
      // Check user's actual roles from memberships
      const hotelMembership = user?.memberships?.find((membership) => membership.org_id === selectedHotel?.id)
      const userRoles = hotelMembership ? [hotelMembership.role] : user?.memberships?.map(m => m.role) || []
      const hasRequiredRole = roles.some(role => userRoles.includes(role))
      
      if (!hasRequiredRole) {
        return <Navigate to="/dashboard" replace />
      }
    }

    if (feature && (user?.memberships?.length ?? 0) > 0) {
      const hotelMembership = user?.memberships?.find((membership) => membership.org_id === selectedHotel?.id)
      const hasFeatureAccess = hotelMembership
        ? canAccessFeature(hotelMembership.role, feature)
        : user?.memberships?.some((membership) => canAccessFeature(membership.role, feature))

      if (!hasFeatureAccess) {
        return <Navigate to="/dashboard" replace />
      }

      // Premium features (DNR, Studio Management, Inventory) require the hotel to
      // be on a paid plan — locked for every member of a free-only hotel. Show a
      // branded spinner until the plan check resolves so we never flash the
      // wrong screen.
      if (isPremiumFeature(feature)) {
        if (!selectedHotelPremiumResolved) {
          return <LogoSpinner />
        }
        if (!selectedHotelIsPremium) {
          return <PremiumLocked featureName={featureLabel(feature)} />
        }
      }
    }

    // User has hotel access and required role (if any), render children
    return <>{children}</>
  }

  // User is authenticated but has no hotel access yet — send them to onboarding
  // to either request access to an existing hotel or create a new one.
  return <Navigate to="/onboarding" replace />
}
